/**
 * Centralized Dialect Configuration
 * Single source of truth for all dialect-related data
 * Follows Stage webapp coding standards from PROJECT_KNOWLEDGE.md
 */

import { DialectsEnum } from "@/constants/dialects.enum";
import { STAGE_CONTACT_NUMBER } from "@/constants/contact";

export interface DialectConfig {
  enum: DialectsEnum;
  abbreviation: string; // 3-letter abbreviation (har, raj, bho, guj, mar)
  iso639Code: string; // ISO 639 language code for SEO
  names: {
    en: string; // English name
    hi: string; // Hindi name
  };
  supportPhone: string;
  imageUrl: string;
  enabled: boolean; // Feature flag for gradual rollout
  sitemapPrefix: string; // Prefix for sitemap URLs (hr, rj, bho, guj, mar)
}

/**
 * Master dialect configuration
 * Adding a new dialect: Add entry here and to DialectsEnum
 *
 * SECURITY: Object.freeze() prevents:
 * - Prototype pollution attacks (__proto__, constructor)
 * - Config mutation/deletion
 * - Unauthorized modifications
 */
export const DIALECT_CONFIGS: Readonly<
  Record<DialectsEnum, Readonly<DialectConfig>>
> = Object.freeze({
  [DialectsEnum.HARYANVI]: Object.freeze({
    enum: DialectsEnum.HARYANVI,
    abbreviation: "har",
    iso639Code: "bgc",
    names: Object.freeze({ en: "Haryanvi", hi: "हरियाणवी" }),
    supportPhone: STAGE_CONTACT_NUMBER,
    imageUrl: "Haryanvi.png",
    enabled: true,
    sitemapPrefix: "hr",
  }),
  [DialectsEnum.RAJASTHANI]: Object.freeze({
    enum: DialectsEnum.RAJASTHANI,
    abbreviation: "raj",
    iso639Code: "raj",
    names: Object.freeze({ en: "Rajasthani", hi: "राजस्थानी" }),
    supportPhone: STAGE_CONTACT_NUMBER,
    imageUrl: "Rajasthani.png",
    enabled: true,
    sitemapPrefix: "rj",
  }),
  [DialectsEnum.BHOJPURI]: Object.freeze({
    enum: DialectsEnum.BHOJPURI,
    abbreviation: "bho",
    iso639Code: "bho",
    names: Object.freeze({ en: "Bhojpuri", hi: "भोजपुरी" }),
    supportPhone: STAGE_CONTACT_NUMBER,
    imageUrl: "Bhojpuri.png",
    enabled: true,
    sitemapPrefix: "bho",
  }),
  [DialectsEnum.GUJARATI]: Object.freeze({
    enum: DialectsEnum.GUJARATI,
    abbreviation: "guj",
    iso639Code: "gu",
    names: Object.freeze({ en: "Gujarati", hi: "ગુજરાતી" }),
    supportPhone: STAGE_CONTACT_NUMBER,
    imageUrl: "Gujarati.png",
    enabled: true,
    sitemapPrefix: "guj",
  }),
});

/**
 * Helper: Get only enabled dialects
 * Used for filtering dialects based on feature flags
 */
export const getEnabledDialects = (): DialectConfig[] => {
  return Object.values(DIALECT_CONFIGS).filter((d) => d.enabled);
};

/**
 * Helper: Get dialect configuration by enum
 *
 * SECURITY: Validates input to prevent prototype pollution
 * - Blocks __proto__, constructor, prototype access
 * - Returns undefined for invalid/malicious inputs
 *
 * BACKWARD COMPATIBILITY: Valid enum values return config as before
 */
export const getDialectConfig = (
  dialect: DialectsEnum,
): DialectConfig | undefined => {
  // Prototype pollution protection
  if (!Object.prototype.hasOwnProperty.call(DIALECT_CONFIGS, dialect)) {
    return undefined;
  }
  return DIALECT_CONFIGS[dialect];
};

/**
 * Helper: Get ISO 639 code by dialect enum
 * Used in schema.utils.ts for SEO metadata
 *
 * SECURITY: Safe property access prevents crashes on invalid input
 * Returns undefined instead of throwing TypeError
 *
 * BACKWARD COMPATIBILITY: Valid enum values return string as before
 */
export const getISO639Code = (dialect: DialectsEnum): string | undefined => {
  const config = getDialectConfig(dialect);
  return config?.iso639Code;
};

/**
 * Helper: Get abbreviation by dialect enum
 * Used for API calls and culture mapping
 *
 * SECURITY: Safe property access prevents crashes on invalid input
 * Returns undefined instead of throwing TypeError
 *
 * BACKWARD COMPATIBILITY: Valid enum values return string as before
 */
export const getDialectAbbreviation = (
  dialect: DialectsEnum,
): string | undefined => {
  const config = getDialectConfig(dialect);
  return config?.abbreviation;
};

/**
 * Helper: Generate dialectToISOMap for schema.utils.ts
 * Supports lookups by enum, English name, and Hindi name
 * Maintains backward compatibility with existing code
 */
export const generateDialectToISOMap = (): Record<string, string> => {
  const map: Record<string, string> = {};

  Object.values(DIALECT_CONFIGS).forEach((config) => {
    const iso = config.iso639Code;

    // 1) Enum key (e.g., "haryanvi") - already lowercase, no need to transform
    map[config.enum] = iso;

    // 2) English name (e.g., "Haryanvi") - normalize to lowercase for case-insensitive lookup
    const enName = config.names.en?.trim();
    if (enName) {
      map[enName.toLowerCase()] = iso;
    }

    // 3) Hindi name (e.g., "हरियाणवी") - normalize to lowercase for case-insensitive lookup
    const hiName = config.names.hi?.trim();
    if (hiName) {
      map[hiName.toLowerCase()] = iso;
    }
  });

  return map;
};

/**
 * Helper: Generate ContentToUserCultureMap for constants.ts
 * Maintains backward compatibility with existing code
 */
export const generateContentToUserCultureMap = (): Record<
  DialectsEnum,
  string
> => {
  const map = {} as Record<DialectsEnum, string>;
  Object.entries(DIALECT_CONFIGS).forEach(([key, config]) => {
    map[key as DialectsEnum] = config.abbreviation;
  });
  return map;
};

/**
 * Helper: Generate supportPhoneNumbers for constants.ts
 * Maintains backward compatibility with existing code
 */
export const generateSupportPhoneNumbers = (): Record<DialectsEnum, string> => {
  const map = {} as Record<DialectsEnum, string>;
  Object.entries(DIALECT_CONFIGS).forEach(([key, config]) => {
    map[key as DialectsEnum] = config.supportPhone;
  });
  return map;
};

/**
 * Helper: Generate dialect name translations for fallbackCopies
 * Returns object with dialect enum keys mapped to localized names
 */
export const generateDialectNamesFallback = (
  lang: "en" | "hi",
): Record<string, string> => {
  const map: Record<string, string> = {};
  Object.values(DIALECT_CONFIGS).forEach((config) => {
    map[config.enum] = config.names[lang];
  });
  return map;
};
